import {
  MentionToViewMapper,
  SelfMentionToViewMapper,
  TextDescriptionToViewMapper,
} from 'components/dataProviders/withIssue/eventDescription';
import {
  IncomingEmailMessageOnView,
  IssueEventOnView,
} from 'components/dataProviders/withIssue/model';
import { GetUserOnViewWithAccessLevelOnIconViewById } from 'shared/domain/user/types/view';
import { IntlShape } from 'react-intl';
import {
  EventDescriptionPartModel,
  IncomingEmailMessageModel,
  IssueEventModel,
  IssueModel,
  MentionModel,
  TextDescriptionModel,
} from 'shared/domain/issue/issueModel';
import { UserAccess } from 'shared/domain/issue/mapping/accessLevel/toOnView/types';
import { SiteModel } from 'shared/domain/site/types/model';
import { userWithPersonalDataToUserOnViewWithIssueAccessLevel } from 'shared/domain/user/mapping/toView';
import { OnIssueViewBase } from 'shared/types/commonView';
import { ProcessType } from 'shared/domain/process/types/model';
import { EventDescriptionType } from 'shared/types/eventDescription';
import { stringToViewDate } from 'shared/utils/date/stringToViewDate';

export function EventDescriptionPartModelToOnView(
  description: EventDescriptionPartModel,
  currentUserId: string,
  issueId: string,
  siteId: string,
  processId: string,
  userAccesses: UserAccess[],
  getUserOnViewWithAccessLevelOnIconViewById: GetUserOnViewWithAccessLevelOnIconViewById,
  processes: ProcessType[],
  sites: SiteModel[]
):
  | TextDescriptionToViewMapper
  | MentionToViewMapper
  | SelfMentionToViewMapper
  | null {
  switch (description.type) {
    case EventDescriptionType.text:
      return new TextDescriptionToViewMapper(
        description as TextDescriptionModel
      );
    case EventDescriptionType.mention:
      const mention = description as MentionModel;

      const userOnViewWithIssueAccessLevel =
        getUserOnViewWithAccessLevelOnIconViewById(
          mention.user,
          userAccesses,
          sites,
          processes
        );
      if (!userOnViewWithIssueAccessLevel) {
        return null;
      }

      return mention.user === currentUserId
        ? new SelfMentionToViewMapper({
            type: mention.type,
            user: userOnViewWithIssueAccessLevel,
            issueId,
            userAccesses,
            siteId,
            processId,
          })
        : new MentionToViewMapper({
            type: mention.type,
            user: userOnViewWithIssueAccessLevel,
            issueId,
            userAccesses,
            siteId,
            processId,
          });
  }
}

export function issueEventModeltoOnView(
  event: IssueEventModel,
  issue: IssueModel,
  currentUserId: string,
  issueId: string,
  siteId: string,
  processId: string,
  userAccesses: UserAccess[],
  organizationId: string,
  intl: IntlShape,
  getUserOnViewWithAccessLevelOnIconViewById: GetUserOnViewWithAccessLevelOnIconViewById,
  processes: ProcessType[],
  sites: SiteModel[]
): IssueEventOnView {
  return {
    ...eventModelToViewBase(
      event,
      organizationId,
      intl,
      issue,
      processes,
      sites
    ),
    title: event.title,
    type: event.type,
    description: event.description.reduce<
      (
        | TextDescriptionToViewMapper
        | MentionToViewMapper
        | SelfMentionToViewMapper
      )[]
    >((descriptionPartsOnView, partModel) => {
      const part = EventDescriptionPartModelToOnView(
        partModel,
        currentUserId,
        issueId,
        siteId,
        processId,
        userAccesses,
        getUserOnViewWithAccessLevelOnIconViewById,
        processes,
        sites
      );
      if (part !== null) {
        descriptionPartsOnView.push(part);
      }

      return descriptionPartsOnView;
    }, []),
    loading: false,
  };
}

function eventModelToViewBase(
  event: IssueEventModel | IncomingEmailMessageModel,
  organizationId: string,
  intl: IntlShape,
  issue: IssueModel,
  processes: ProcessType[],
  sites: SiteModel[]
): OnIssueViewBase {
  const createdBy = event.createdBy
    ? userWithPersonalDataToUserOnViewWithIssueAccessLevel(
        event.createdBy,
        organizationId,
        intl,
        issue.userAccesses,
        processes,
        sites
      )
    : null;
  const modifiedBy = event.modifiedBy
    ? userWithPersonalDataToUserOnViewWithIssueAccessLevel(
        event.modifiedBy,
        organizationId,
        intl,
        issue.userAccesses,
        processes,
        sites
      )
    : null;

  return {
    _id: event._id,
    createdBy: createdBy,
    createdAt: stringToViewDate(event.createdAt),
    modifiedAt: stringToViewDate(event.modifiedAt),
    modifiedBy: modifiedBy,
    deleted: !!event.deleted,
  };
}

export function incomingEmailMessageModelToOnView(
  message: IncomingEmailMessageModel,
  organizationId: string,
  intl: IntlShape,
  issue: IssueModel,
  processes: ProcessType[],
  sites: SiteModel[]
): IncomingEmailMessageOnView {
  const onViewBase = eventModelToViewBase(
    message,
    organizationId,
    intl,
    issue,
    processes,
    sites
  );

  return {
    ...onViewBase,
    subject: message.subject,
    incomingEmailMessageContent: message.incomingEmailMessageContent,
  };
}
